import { Address } from '@dehaat/kisan-app-bl/models/AddressV2';
import { Vendor } from '@dehaat/kisan-app-bl/models/Vendor';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect } from 'react';
import { AVAILABLE_STOCK, DEFAULT_CURRENCY, OUT_OF_STOCK_PRODUCTS } from '@/constants/common';
import { CartContext } from '@/context/CartProvider';
import { FreeInsuranceQuota, InsuranceDataItem } from '@/models/api/productInsurance';
import { ProductOutOfStock } from '@/models/Cart';
import { ProductVariant } from '@/models/Product';
import { ProductListTypeEnum } from '@/utils/analytics/types';
import { productCatalogueId } from '@/utils/cart';
import { isArray, isSessionStorageAvailable } from '@/utils/helper';
import { getCartUpdatePricing } from '@/utils/helpers/flashSale';
import Caption, { VARIANT as CAPTION_VARIANT } from '../Caption';
import Paragraph, { VARIANT as PARAGRAPH_VARIANT } from '../Paragraph';
import AddToCartNew from '../products/AddToCartNew';
import InsuranceLabel from '../products/InsuranceLabel';
import ProductImage from '../products/ProductImage';
import ProductPrice from '../products/ProductPrice';
const Insurance = dynamic(() => import('./insurance'), {
  ssr: false
});
interface Props {
  insuranceData: InsuranceDataItem[];
  insuranceFreeQuota: Record<string, FreeInsuranceQuota>;
  position: number;
  productVariant: ProductVariant;
  selectedAddress: Address | null;
  showAddressList: VoidFunction;
  showingAddressList: boolean;
  vendor: Vendor | null;
  availableQty?: number;
  handleOutOfProduct: (item: ProductOutOfStock) => void;
}
const ProductCard = ({
  insuranceData,
  insuranceFreeQuota,
  position,
  productVariant,
  selectedAddress,
  showAddressList,
  showingAddressList,
  vendor,
  availableQty,
  handleOutOfProduct
}: Props) => {
  const {
    product,
    attribute_value,
    insuranceData: variantInsuranceData
  } = productVariant;
  const {
    t
  } = useTranslation('products');
  const {
    cartItems,
    updateCart
  } = useContext(CartContext);
  const quantity = cartItems[productVariant.id] || 0;
  const isProductOutOfStock = availableQty === 0;
  const variantName = isArray(attribute_value) ? attribute_value[0].name : '';
  const StockInSS = typeof window !== 'undefined' && sessionStorage?.getItem(AVAILABLE_STOCK);
  const availableStock = StockInSS ? JSON.parse(StockInSS) : undefined;

  // Remove item from cart is availableQty is zero
  // Set item's qty to availableQty is qty > availableQty
  useEffect(() => {
    if (isProductOutOfStock) {
      if (isSessionStorageAvailable(window)) {
        const items = sessionStorage.getItem(OUT_OF_STOCK_PRODUCTS);
        const outOfStockItems: ProductOutOfStock[] = items ? JSON.parse(items) : [];
        if (!outOfStockItems.some(item => item.id == productVariant.id)) {
          const item: ProductOutOfStock = {
            id: productVariant.id,
            productName: productVariant.product?.name,
            productImage: productVariant.product?.image_url,
            brandName: productVariant.product?.brand?.name,
            quantity: isArray(attribute_value) ? attribute_value[0].name : ''
          };
          outOfStockItems.push(item);
          handleOutOfProduct(item);
        }
        sessionStorage.setItem(OUT_OF_STOCK_PRODUCTS, JSON.stringify(outOfStockItems));
      }
      updateCart({
        variantId: productVariant.id,
        quantity: quantity,
        ...getCartUpdatePricing(productVariant),
        catalogueId: productCatalogueId(productVariant.inventory_set),
        groupId: {
          parent_type: productVariant.product?.parent_type,
          parent_id: productVariant.product?.parent_id
        }
      }, false);
    }
    if (!!availableQty && quantity > availableQty) {
      updateCart({
        variantId: productVariant.id,
        quantity: quantity - availableQty,
        ...getCartUpdatePricing(productVariant),
        catalogueId: productCatalogueId(productVariant.inventory_set),
        groupId: {
          parent_type: productVariant.product?.parent_type,
          parent_id: productVariant.product?.parent_id
        }
      }, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProductOutOfStock, updateCart, availableQty]);
  return product && !isProductOutOfStock ? <section className="mx-4 py-5 border border-transparent border-b-neutral-10 last:border-b-transparent" data-sentry-component="ProductCard" data-sentry-source-file="ProductCard.tsx">
      <Link className="flex items-center" href={`/products/${product.id}`} data-sentry-element="Link" data-sentry-source-file="ProductCard.tsx">
        <div className="flex flex-col items-center grow-0 shrink-0">
          <ProductImage imageUrl={product.image_url} productName={product.name} data-sentry-element="ProductImage" data-sentry-source-file="ProductCard.tsx" />
          {variantInsuranceData && variantInsuranceData.hasInsurance && vendor != null ? <InsuranceLabel className="z-10 -mt-1.5" /> : null}
        </div>
        <div className="flex-1 ml-4">
          {product && product.brand ? <Caption variant={CAPTION_VARIANT.ONE} className="font-nato-medium text-neutral-50" label={product.brand.name} /> : null}
          <Paragraph variant={PARAGRAPH_VARIANT.BIG} isHighlighted className="text-neutral-90" data-sentry-element="Paragraph" data-sentry-source-file="ProductCard.tsx">
            {product.name}
          </Paragraph>

          <div className="flex items-end">
            <div className="flex-1">
              <Paragraph variant={PARAGRAPH_VARIANT.BIG} isHighlighted className="text-neutral-90" testId="product-variant-name" data-sentry-element="Paragraph" data-sentry-source-file="ProductCard.tsx">
                {variantName}
              </Paragraph>

              <ProductPrice variant={productVariant} hideFlashSaleDesign data-sentry-element="ProductPrice" data-sentry-source-file="ProductCard.tsx" />
            </div>
            {isArray(productVariant.inventory_set) ? <div className="h-10 grow-0 shrink-0">
                <AddToCartNew productVariant={productVariant} listType={ProductListTypeEnum.CART} index={position} productId={Number(product.id)} productName={product.name} productBrand={product.brand?.name} productCategory={product.category?.name} />
              </div> : null}
          </div>
        </div>
      </Link>
      {variantInsuranceData && variantInsuranceData.hasInsurance && vendor != null ? <Insurance insuranceData={insuranceData} insuranceFreeQuota={insuranceFreeQuota} variant={productVariant} address={selectedAddress} showAddressList={showAddressList} showingAddressList={showingAddressList} position={position} /> : null}
      {availableStock?.[productVariant?.id] > 0 && <div className="bg-error-10 mt-4 px-4 py-3 rounded-lg text-error-100 text-center">
          {availableStock[productVariant.id] === 1 ? t('only_one_item_left') : t('only_few_items_left', {
        availableQty: availableStock[productVariant.id]
      })}
        </div>}
    </section> : null;
};
export default ProductCard;